import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserBCPs, BCP, analyzeBCP, AnalysisStruct } from '../services/api';
import AIAnalysisModal from '../components/BCP/AIAnalysisModal';
import DocumentPreview from '../components/BCP/DocumentPreview';
import ISO27001Modal from '../components/BCP/ISO27001Modal';
import BCPCreationButton from '../components/BCP/BCPCreationButton';
import WorkspaceLayout from '../components/Workspace/WorkspaceLayout';
import { useBCPHeaderActions } from '../contexts/BCPHeaderActionsContext';
import { calculateOverallRiskScore, RiskScore } from '../utils/riskCalculator';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

const CREATE_BCP_BUTTON_CLASS =
  'inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';

const BCPList: React.FC = () => {
  const navigate = useNavigate();
  const [bcps, setBcps] = useState<BCP[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzingBCP, setAnalyzingBCP] = useState<number | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisStruct | null>(null);
  const [analyzedBCPName, setAnalyzedBCPName] = useState<string>('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBCPId, setPreviewBCPId] = useState<number | null>(null);
  const [previewBCPName, setPreviewBCPName] = useState<string>('');
  const [showISO27001Modal, setShowISO27001Modal] = useState(false);
  const [iso27001BCPId, setIso27001BCPId] = useState<number | null>(null);
  const [iso27001BCPName, setIso27001BCPName] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [riskScore, setRiskScore] = useState<RiskScore | null>(null);

  const { canAccessFeature } = useSubscriptionContext();
  const { user } = useAuth();
  const { setActions } = useBCPHeaderActions();

  const stats = {
    totalBCPs: bcps.length,
    inProgressBCPs: bcps.filter(bcp => bcp.status === 'draft' || bcp.status === 'in_review').length,
    completedBCPs: bcps.filter(bcp =>
      bcp.status === 'completed' || bcp.status === 'approved' || bcp.status === 'document_ready'
    ).length,
    riskScore: riskScore?.level || 'N/A'
  };

  useEffect(() => {
    const fetchBCPs = async () => {
      try {
        setIsLoading(true);
        const data = await getUserBCPs();
        setBcps(data);

        // Calculate risk score when BCPs are loaded
        const calculatedRiskScore = calculateOverallRiskScore(data);
        setRiskScore(calculatedRiskScore);
      } catch (err: any) {
        console.error('Error fetching BCPs:', err);
        setError(err.response?.data?.detail || 'Failed to load BCPs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBCPs();
  }, []);

  useEffect(() => {
    setActions(
      <BCPCreationButton className={CREATE_BCP_BUTTON_CLASS}>
        <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        New BCP
      </BCPCreationButton>
    );
    return () => setActions(null);
  }, [setActions]);

  // Handle AI analysis for a specific BCP
  const handleAIAnalysis = async (bcpId: number) => {
    try {
      setAnalyzingBCP(bcpId);
      console.log(`🚀 [BCP LIST] Starting AI analysis for BCP ID: ${bcpId}`);

      // Get BCP name for the modal
      const bcp = bcps.find(b => b.id === bcpId);
      if (bcp) {
        setAnalyzedBCPName(bcp.business_name);
      }

      // Call the AI analysis endpoint using the API service
      const result = await analyzeBCP(bcpId);
      console.log(`✅ [BCP LIST] AI analysis completed for BCP ID: ${bcpId}`, result);

      // Store analysis results and show modal
      setAnalysisResults(result.analysis);
      setShowAnalysisModal(true);

      // Refresh BCPs to show updated status
      const updatedBcps = await getUserBCPs();
      setBcps(updatedBcps);
    } catch (error) {
      console.error(`❌ [BCP LIST] AI analysis failed for BCP ID: ${bcpId}`, error);
      alert(`AI analysis failed. Please try again.`);
    } finally {
      setAnalyzingBCP(null);
    }
  };

  // Filter BCPs based on status and search term
  const filteredBCPs = bcps.filter(bcp => {
    const matchesStatus = filterStatus === 'all' || bcp.status === filterStatus;
    const matchesSearch = searchTerm === '' ||
      bcp.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bcp.industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bcp.business_type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
      case 'document_ready':
        return 'bg-green-100 text-green-800';
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'approved':
        return 'Approved';
      case 'document_ready':
        return 'PDF ready';
      case 'in_review':
        return 'In Review';
      case 'draft':
        return 'Draft';
      default:
        return status;
    }
  };

  return (
    <WorkspaceLayout
      title="Continuity plan"
      subtitle="Manage and create business continuity plans for your organization."
      primaryAction={
        <BCPCreationButton className="px-3.5 py-1.5 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold shadow-sm transition-colors flex items-center space-x-1">
          <span>+</span>
          <span>New plan</span>
        </BCPCreationButton>
      }
    >
      <div className="space-y-6">
        {/* Error Display */}
      {error && (
        <div className="p-4 mb-6 bg-red-50 rounded-md border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 mb-8 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total BCPs */}
        <div className="overflow-hidden bg-white rounded-lg shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex justify-center items-center w-8 h-8 bg-blue-500 rounded-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">All plans</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalBCPs}</dd>
                  <dd className="text-xs text-gray-400">Every plan on this account</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Active BCPs */}
        <div className="overflow-hidden bg-white rounded-lg shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex justify-center items-center w-8 h-8 bg-yellow-500 rounded-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">In progress</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.inProgressBCPs}</dd>
                  <dd className="text-xs text-gray-400">Status draft or in_review</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Completed BCPs */}
        <div className="overflow-hidden bg-white rounded-lg shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex justify-center items-center w-8 h-8 bg-green-500 rounded-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">Completed plans</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.completedBCPs}</dd>
                  <dd className="text-xs text-gray-400">completed, document_ready, or approved</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Score */}
        <div className="overflow-hidden bg-white rounded-lg shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`flex justify-center items-center w-8 h-8 rounded-md ${
                  riskScore?.level === 'Low' ? 'bg-green-500' :
                  riskScore?.level === 'Medium' ? 'bg-yellow-500' :
                  riskScore?.level === 'High' ? 'bg-orange-500' :
                  'bg-red-500'
                }`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Risk Score</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {isLoading ? '...' : (riskScore ? `${riskScore.level} (${riskScore.score}/100)` : 'N/A')}
                  </dd>
                </dl>
              </div>
            </div>
            {riskScore && riskScore.recommendations.length > 0 && (
              <div className="group relative mt-3">
                <div className="flex items-center text-xs text-gray-400 cursor-help">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Hover for recommendations
                </div>
                <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  <div className="space-y-1">
                    {riskScore.recommendations.map((rec, index) => (
                      <p key={index} className="text-xs">• {rec}</p>
                    ))}
                  </div>
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="flex-1">
              <label htmlFor="search" className="sr-only">Search BCPs</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by business name, industry, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <label htmlFor="status-filter" className="sr-only">Filter by status</label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="completed">Completed</option>
                <option value="document_ready">PDF ready</option>
                <option value="approved">Approved</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* BCP List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
              <p className="mt-4 text-sm text-gray-500">Loading BCPs...</p>
            </div>
          ) : filteredBCPs.length === 0 ? (
            /* Empty State */
            <div className="py-12 text-center">
              <svg
                className="mx-auto w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm || filterStatus !== 'all' ? 'No BCPs match your filters' : 'No BCPs yet'}
              </h3>
              {searchTerm || filterStatus !== 'all' ? (
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your search or filter criteria.
                </p>
              ) : (
                <div className="mt-6">
                  <BCPCreationButton className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-medium rounded-lg text-sm shadow-xs transition-colors">
                    <span className="text-base font-normal leading-none">+</span>
                    <span>Create first plan</span>
                  </BCPCreationButton>
                </div>
              )}
            </div>
          ) : (
            /* BCP List */
            <div className="space-y-4">
              {filteredBCPs.map((bcp) => (
                <div
                  key={bcp.id}
                  className="flex items-center p-4 rounded-lg border border-gray-200 transition-all duration-200 hover:border-gray-300 hover:shadow-sm group"
                >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h4 className="text-sm font-medium text-gray-900 transition-colors group-hover:text-blue-700">
                          {bcp.business_name}
                        </h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bcp.status)}`}>
                          {getStatusText(bcp.status)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{bcp.industry} • {bcp.business_type}</p>
                      <p className="text-xs text-gray-400">
                        Created {new Date(bcp.created_at).toLocaleDateString()}
                        {bcp.updated_at && bcp.updated_at !== bcp.created_at && (
                          <span> • Updated {new Date(bcp.updated_at).toLocaleDateString()}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center ml-4 space-x-2">
                      <div className="flex items-center space-x-2">
                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.location.href = `/bcp/${bcp.id}`;
                          }}
                          className="px-3 py-1 text-xs text-blue-600 bg-blue-50 rounded-md border border-blue-200 transition-colors hover:bg-blue-100"
                          title="Edit BCP"
                        >
                          <svg className="inline mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>

                        {/* Preview Button */}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPreviewBCPId(bcp.id);
                            setPreviewBCPName(bcp.business_name);
                            setShowPreviewModal(true);
                          }}
                          className="px-3 py-1 text-xs text-green-600 bg-green-50 rounded-md border border-green-200 transition-colors hover:bg-green-100"
                          title="Preview BCP"
                        >
                          <svg className="inline mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Preview
                        </button>

                        {/* ISO 27001 Button - Only for Premium users or admins */}
                        {(canAccessFeature('iso_27001_readiness') || (user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'))) && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIso27001BCPId(bcp.id);
                              setIso27001BCPName(bcp.business_name);
                              setShowISO27001Modal(true);
                            }}
                            className="px-3 py-1 text-xs text-purple-600 bg-purple-50 rounded-md border border-purple-200 transition-colors hover:bg-purple-100"
                            title="ISO 27001 Disaster Recovery Plan"
                          >
                            <svg className="inline mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            ISO 27001
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AI Analysis Modal */}
      {showAnalysisModal && analysisResults && (
        <AIAnalysisModal
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          analysis={analysisResults}
          bcpName={analyzedBCPName}
        />
      )}

      {/* Document Preview Modal */}
      {showPreviewModal && previewBCPId && (
        <DocumentPreview
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          bcpId={previewBCPId}
          bcpName={previewBCPName}
        />
      )}

      {/* ISO 27001 Modal */}
      {showISO27001Modal && iso27001BCPId && (
        <ISO27001Modal
          isOpen={showISO27001Modal}
          onClose={() => setShowISO27001Modal(false)}
          bcpId={iso27001BCPId}
          bcpName={iso27001BCPName}
        />
      )}
      </div>
    </WorkspaceLayout>
  );
};

export default BCPList;
